// Filter bar — every control mutates the single `filters` object owned by the
// page; nothing here refetches (filters are applied client-side in
// safetyFilters.js). Option lists for manner/light/surface derive from the
// loaded crash data so they always match reality.
import React from 'react';
import { distinctValues } from './safetyFilters';

const ANALYSES = [
  { key: 'ALL', label: 'All analyses' },
  { key: 'SSD', label: 'SSD — stopping sight' },
  { key: 'PSD', label: 'PSD — passing sight' },
  { key: 'GLR', label: 'GLR — sun glare' },
  { key: 'FSA', label: 'FSA — flat spot analysis' },
  { key: 'HCA', label: 'HCA — horizontal curves' },
  { key: 'GRW', label: 'GRW — guardrail warrant' },
  { key: 'ISD', label: 'ISD — intersections' },
];
const SEVERITIES = [
  { key: 'fatal', label: 'Fatal' },
  { key: 'injury', label: 'Injury' },
  { key: 'pdo', label: 'PDO' },
  { key: 'unknown', label: 'Unknown' },
];

function MultiSelect({ label, options, selected, onChange }) {
  return (
    <label className="safety-filter">
      <span>{label}</span>
      <select
        multiple
        size={Math.min(4, Math.max(2, options.length))}
        value={selected}
        onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}

export default function FilterBar({ filters, setFilters, crashes, corridorExtent, datasetYears }) {
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const [mpMin, mpMax] = corridorExtent || [null, null];
  const range = filters.mpRange || corridorExtent || [0, 0];

  return (
    <div className="safety-filterbar">
      <label className="safety-filter">
        <span>Analysis</span>
        <select value={filters.analysis} onChange={(e) => set({ analysis: e.target.value })}>
          {ANALYSES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
      </label>

      <div className="safety-filter">
        <span>&nbsp;</span>
        <button
          type="button"
          className={`safety-toggle ${filters.correlatedOnly ? 'on' : ''}`}
          onClick={() => set({ correlatedOnly: !filters.correlatedOnly })}
          title="Keep only crashes with an RDV deficiency finding at their location (the Insights set), and the runs near them"
        >
          {filters.correlatedOnly ? '✓ ' : ''}Correlated only
        </button>
        <button
          type="button"
          className={`safety-toggle ${filters.showCrashes ? '' : 'on'}`}
          onClick={() => set({ showCrashes: !filters.showCrashes })}
          title="Hide the crash layers and tables to review the RDV analyses on their own"
        >
          {filters.showCrashes ? 'Hide crash data' : '✓ Analyses only'}
        </button>
      </div>

      <div className="safety-filter">
        <span>Years</span>
        <div className="safety-checks">
          {/* every year the DATASET holds — a year with zero corridor crashes
              shows "(0)" instead of silently disappearing */}
          {(() => {
            const counts = {};
            (crashes || []).forEach((c) => {
              const y = String(c.year);
              counts[y] = (counts[y] || 0) + 1;
            });
            const all = [...new Set([...(datasetYears || []).map(String),
              ...distinctValues(crashes, 'year')])].sort((a, b) => a - b);
            return all.map((y) => (
              <label key={y} className={counts[y] ? '' : 'safety-year-empty'}
                title={counts[y] ? undefined : 'No crashes on this corridor for this year'}>
                <input
                  type="checkbox"
                  checked={!filters.years.length || filters.years.includes(y)}
                  onChange={(e) => {
                    const current = filters.years.length ? filters.years : all;
                    const next = e.target.checked
                      ? [...new Set([...current, y])]
                      : current.filter((k) => k !== y);
                    set({ years: next.length === all.length ? [] : next });
                  }}
                />
                {y} ({counts[y] || 0})
              </label>
            ));
          })()}
        </div>
      </div>

      <label className="safety-filter">
        <span>Direction</span>
        <select value={filters.direction} onChange={(e) => set({ direction: e.target.value })}>
          <option value="both">Both</option>
          <option value="P">Eastbound / P</option>
          <option value="N">Westbound / N</option>
        </select>
      </label>

      {mpMin !== null && (
        <label className="safety-filter safety-filter-range">
          <span>Milepost {range[0].toFixed(1)} – {range[1].toFixed(1)}</span>
          <span className="safety-range-pair">
            <input type="range" min={mpMin} max={mpMax} step={0.1} value={range[0]}
              onChange={(e) => set({ mpRange: [Math.min(+e.target.value, range[1]), range[1]] })} />
            <input type="range" min={mpMin} max={mpMax} step={0.1} value={range[1]}
              onChange={(e) => set({ mpRange: [range[0], Math.max(+e.target.value, range[0])] })} />
          </span>
        </label>
      )}

      <div className="safety-filter">
        <span>Severity</span>
        <div className="safety-checks">
          {SEVERITIES.map((s) => (
            <label key={s.key}>
              <input
                type="checkbox"
                checked={!filters.severities.length || filters.severities.includes(s.key)}
                onChange={(e) => {
                  const current = filters.severities.length
                    ? filters.severities
                    : SEVERITIES.map((x) => x.key);
                  const next = e.target.checked
                    ? [...new Set([...current, s.key])]
                    : current.filter((k) => k !== s.key);
                  set({ severities: next.length === SEVERITIES.length ? [] : next });
                }}
              />
              {s.label}
            </label>
          ))}
        </div>
      </div>

      <MultiSelect label="Manner" options={distinctValues(crashes, 'manner')}
        selected={filters.manners} onChange={(v) => set({ manners: v })} />
      <MultiSelect label="Light" options={distinctValues(crashes, 'light')}
        selected={filters.lights} onChange={(v) => set({ lights: v })} />
      <MultiSelect label="Surface" options={distinctValues(crashes, 'surface')}
        selected={filters.surfaces} onChange={(v) => set({ surfaces: v })} />

      <div className="safety-filter">
        <span>Mechanism</span>
        <div className="safety-checks safety-checks-col">
          <label>
            <input type="checkbox" checked={filters.excludeAnimal}
              onChange={(e) => set({ excludeAnimal: e.target.checked })} />
            Exclude animal strikes
          </label>
          <label title="FSA: flat cross slope drains poorly — wet / ice / snow surface crashes">
            <input type="checkbox" checked={filters.wetOnly}
              onChange={(e) => set({ wetOnly: e.target.checked })} />
            Wet / snow surface only
          </label>
          <label title="GLR: crash date & time falls inside the location's glare impact ranges (direction-matched)">
            <input type="checkbox" checked={filters.glareOnly}
              onChange={(e) => set({ glareOnly: e.target.checked })} />
            Glare-matched only
          </label>
          <label title="GRW: vehicle went over the embankment / overturned">
            <input type="checkbox" checked={filters.embankmentOnly}
              onChange={(e) => set({ embankmentOnly: e.target.checked })} />
            Embankment / overturn only
          </label>
        </div>
      </div>

      <button
        type="button"
        className="safety-btn"
        onClick={() => setFilters((f) => ({
          ...f, mpRange: null, years: [], severities: [], manners: [], lights: [], surfaces: [],
          excludeAnimal: false, wetOnly: false, glareOnly: false, embankmentOnly: false,
          correlatedOnly: false, showCrashes: true, dateRange: null, direction: 'both',
        }))}
      >
        Reset filters
      </button>
    </div>
  );
}
